import type { Ref } from 'vue';

export interface EditorInitialOpenProjectLoadCoordinatorOptions {
  /** 首屏 OpenProject 辅助数据统一 loading 的响应式出口。 */
  readonly auxiliaryLoading: Ref<boolean>;
}

/**
 * `EditorInitialOpenProjectLoadCoordinator`
 *
 * 收口编辑器首屏 OpenProject 后的辅助数据加载生命周期。
 *
 * 设计说明：
 * 1. 首屏打开项目时，模型、设计树、提交记录、装配节点列表需要作为一个事务向用户呈现 loading；
 * 2. 历史提交切换也会触发 OpenProject，但它不是首屏事务，不能因此刷新提交记录；
 * 3. 通过 requestId 防止过期首屏请求在完成后关闭新请求的 loading 状态。
 */
export class EditorInitialOpenProjectLoadCoordinator {
  private readonly auxiliaryLoading: Ref<boolean>;

  private activeRequestId: number | null = null;

  constructor(options: EditorInitialOpenProjectLoadCoordinatorOptions) {
    this.auxiliaryLoading = options.auxiliaryLoading;
  }

  /**
   * 标记一次首屏 OpenProject 事务开始，并点亮所有辅助面板 loading。
   *
   * @param requestId 首屏加载事务编号。
   */
  begin(requestId: number): void {
    this.activeRequestId = requestId;
    this.auxiliaryLoading.value = true;
  }

  /**
   * 判断当前是否仍处于首屏辅助数据统一 loading。
   *
   * @returns 首屏辅助数据是否仍在加载。
   */
  isLoading(): boolean {
    return this.auxiliaryLoading.value;
  }

  /**
   * 释放指定首屏事务持有的辅助 loading。
   *
   * @param requestId 首屏加载事务编号。
   * @returns 成功释放当前事务时返回 true，过期事务返回 false。
   */
  finish(requestId: number): boolean {
    if (this.activeRequestId !== requestId) {
      return false;
    }

    this.activeRequestId = null;
    this.auxiliaryLoading.value = false;
    return true;
  }

  /**
   * 主动取消首屏事务，通常发生在编辑器卸载或新事务抢占时。
   */
  cancel(): void {
    this.activeRequestId = null;
    this.auxiliaryLoading.value = false;
  }
}
